import { throttle } from "lodash-es";
import { useCallback, useEffect } from "react";

import { fetchUser } from "@/lib/user";
import { logout } from "../api";
import { useSetTimeoutWarning } from "../components/TimeoutWarningDialog";
import Worker from "../worker/idle.worker.js?worker";

const checkSessionInterval = 60000;
const worker = new Worker();

async function checkSession() {
  const user = await fetchUser(true);
  if (!user) {
    logout(true);
  }
}

/**
 * Check session every `checkSessionInterval`. This should be triggered on
 * some user action to refresh the session cookie while also honoring the
 * idle timeout. This is required to refresh the cookie as many GraphQL
 * calls get cached which results in no network activity to refresh the session.
 */
const throttledCheckSession = throttle(checkSession, checkSessionInterval, {
  leading: false,
});

function start(timeout: number) {
  worker.postMessage({ action: "start", timeout });
}

function stop() {
  worker.postMessage({ action: "stop" });
}

export function useIdleTimeout(timeout = 600000) {
  const resetTimer = throttle(() => {
    start(timeout);
  }, 1000);

  const [warningShown, showWarningDialog] = useSetTimeoutWarning();

  const onActivity = useCallback(() => {
    if (!warningShown) {
      resetTimer();
      throttledCheckSession();
    }
  }, [resetTimer, warningShown]);

  const onVisible = useCallback(() => {
    if (document.visibilityState === "visible") {
      onActivity();
    }
  }, [onActivity]);

  const onMessage = useCallback(
    (evt: MessageEvent) => {
      if (evt.data === "warn") {
        showWarningDialog(true);
      } else if (evt.data === "logout") {
        logout(true);
      }
    },
    [showWarningDialog],
  );

  const addEventListeners = useCallback(() => {
    worker.addEventListener("message", onMessage);

    window.addEventListener("load", onActivity);
    window.addEventListener("mousedown", onActivity);
    window.addEventListener("touchstart", onActivity);
    window.addEventListener("click", onActivity);
    window.addEventListener("keypress", onActivity);
    window.addEventListener("scroll", onActivity, true);

    document.addEventListener("visibilitychange", onVisible);
  }, [onActivity, onMessage, onVisible]);

  const removeEventListeners = useCallback(() => {
    worker.removeEventListener("message", onMessage);

    window.removeEventListener("load", onActivity);
    window.removeEventListener("mousedown", onActivity);
    window.removeEventListener("touchstart", onActivity);
    window.removeEventListener("click", onActivity);
    window.removeEventListener("keypress", onActivity);
    window.removeEventListener("scroll", onActivity, true);

    document.removeEventListener("visibilitychange", onVisible);
  }, [onActivity, onMessage, onVisible]);

  useEffect(() => {
    start(timeout);
    addEventListeners();
    return () => {
      stop();
      removeEventListeners();
    };
  }, [timeout, addEventListeners, removeEventListeners]);
}
